import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { socket } from '../../socket';
import Header from '../Comman/Header';
import { LuArrowLeft, LuSend, LuMessageSquare, LuUser, LuInfo, LuImage, LuX } from 'react-icons/lu';
import { Link, useParams } from 'react-router-dom';

function TicketDetails() {
    const params = useParams();
    const ticketId = params.ticketId || params.id;

    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [image, setImage] = useState(null);
    const [imageBase64, setImageBase64] = useState('');
    const chatEndRef = useRef(null);
    const username = localStorage.getItem("username");


    const fetchMessages = async () => {
        if (!ticketId || !username) return;
        try {
            const res = await axios.post(process.env.REACT_APP_BACKEND_LINK, {
                method: "support",
                submethod: "getmsghistory",
                role: "user",
                userAddress: username,
                ticketId: ticketId,
                key: process.env.REACT_APP_KEY,
            });
            if (res.data?.resCode === 200) {
                setMessages(res.data.messages || []);
            }
        } catch (err) {
            console.error("API Error fetching messages:", err);
        }
    };

    useEffect(() => {
        fetchMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId, username]);

    // Setup Socket connection and Real-time listener
    useEffect(() => {
        if (!ticketId) return;

        if (!socket.connected) {
            socket.connect();
        }

        const handleConnect = () => {
            socket.emit("joinTicket", ticketId);
        };

        const handleNewMessage = (msg) => {
            setMessages((prev) => {
                // Prevent duplicate messages in ui
                if (prev.some(p => p._id === msg._id)) return prev;
                return [...prev, msg];
            });
        };

        if (socket.connected) {
            handleConnect();
        }

        socket.on("connect", handleConnect);
        socket.on("newMessage", handleNewMessage);

        return () => {
            socket.off("connect", handleConnect);
            socket.off("newMessage", handleNewMessage);
        };
    }, [ticketId]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert("Image size must be less than 5MB");
                return;
            }
            if (!file.type.startsWith("image/")) {
                alert("Only image files are allowed");
                return;
            }
            setImage(file);
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                setImageBase64(reader.result);
            };
        }
    };

    const removeImage = () => {
        setImage(null);
        setImageBase64("");
    };

    const handleSend = async () => {
        if ((!inputValue.trim() && !imageBase64) || !ticketId || !username) return;
        const messageContent = inputValue;
        const attachedImage = imageBase64;

        setInputValue('');
        setImage(null);
        setImageBase64('');

        try {
            const res = await axios.post(process.env.REACT_APP_BACKEND_LINK, {
                method: "support",
                submethod: "sendmsg",
                role: "user",
                userAddress: username,
                ticketId: ticketId,
                content: messageContent,
                image: attachedImage,
                key: process.env.REACT_APP_KEY,
            });
            if (res.data?.resCode !== 200) {
                console.error(res.data?.message);
            }
            fetchMessages();
        } catch (err) {
            console.error("API Error sending message:", err);
        }
    };

    const CSS = `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        .db-scene {
            background: #e8ecf1;
            min-height: 100vh;
            font-family: 'Inter', sans-serif;
            padding: 28px 20px;
            color: #1e293b;
        }

        .back-nav {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 24px;
            padding: 0 10px;
        }

        .btn-back {
            background: rgba(255, 255, 255, 0.6);
            border: 1px solid rgba(255, 255, 255, 0.8);
            border-radius: 12px;
            padding: 10px 20px;
            color: #64748b;
            font-size: 11px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1.5px;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 10px;
            box-shadow: 2px 5px 15px rgba(0,0,0,0.03);
            transition: all 0.3s;
        }

        .btn-back:hover {
            background: #fff;
            transform: translateX(-3px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.05);
        }

        .ticket-id-tag {
            font-size: 11px;
            font-weight: 800;
            color: #006fe3;
            letter-spacing: 1px;
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .status-dot {
            width: 8px;
            height: 8px;
            background: #10b981;
            border-radius: 50%;
            box-shadow: 0 0 10px rgba(16, 185, 129, 0.4);
        }

        .chat-container {
            background: rgba(255, 255, 255, 0.7);
            backdrop-filter: blur(25px);
            border: 1px solid rgba(255, 255, 255, 0.5);
            border-radius: 44px;
            padding: 0;
            box-shadow: 1px 20px 20px 0px #bfc9d8, 0 10px 20px rgba(0, 0, 0, 0.05), inset 0 1px 0 #ffffff;
            display: flex;
            flex-direction: column;
            height: calc(100vh - 200px);
            min-height: 550px;
            overflow: hidden;
            transition: all 0.3s ease;
        }

        .chat-header {
            padding: 24px 30px;
            border-bottom: 1px solid rgba(0,0,0,0.03);
            display: flex;
            align-items: center;
            gap: 15px;
        }

        .chat-icon-box {
            width: 48px;
            height: 48px;
            background: rgba(0, 111, 227, 0.1);
            border: 1px solid rgba(0, 111, 227, 0.2);
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #006fe3;
            font-size: 20px;
            box-shadow: 0 8px 16px rgba(0, 111, 227, 0.05);
        }

        .header-text h2 {
            font-size: 18px;
            font-weight: 900;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            margin: 0;
            color: #1e293b;
        }

        .header-text p {
            font-size: 10px;
            font-weight: 700;
            color: #94a3b8;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin: 2px 0 0 0;
        }

        .chat-body {
            flex: 1;
            padding: 30px;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            gap: 24px;
        }

        /* --- MESSAGE BUBBLES --- */
        .msg-row {
            display: flex;
            width: 100%;
            gap: 12px;
        }

        .msg-user { justify-content: flex-end; }
        .msg-admin { justify-content: flex-start; }

        .bubble-wrap {
            display: flex;
            flex-direction: column;
            max-width: 80%;
        }
        .msg-user .bubble-wrap { align-items: flex-end; }
        .msg-admin .bubble-wrap { align-items: flex-start; }

        .bubble {
            padding: 14px 22px;
            border-radius: 18px;
            font-size: 14px;
            font-weight: 500;
            line-height: 1.5;
            position: relative;
            box-shadow: 0 4px 12px rgba(0,0,0,0.02);
            word-break: break-all;
        }

        .msg-user .bubble {
            background: #ffffff;
            color: #1e293b;
            border: 1px solid rgba(0, 111, 227, 0.2);
            border-bottom-right-radius: 4px;
            box-shadow: 0 10px 20px rgba(0, 111, 227, 0.05);
        }

        .msg-admin .bubble {
            background: #ffffff;
            color: #1e293b;
            border: 1px solid #f1f5f9;
            border-bottom-left-radius: 4px;
            box-shadow: 0 10px 25px rgba(0,0,0,0.03);
        }

        .msg-meta {
            font-size: 9px;
            font-weight: 800;
            color: #c9d1d9;
            text-transform: uppercase;
            margin-top: 6px;
            letter-spacing: 0.5px;
        }

        .avatar-box {
            width: 40px;
            height: 40px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
            flex-shrink: 0;
            margin-top: auto;
        }

        .msg-user .avatar-box {
            background: rgba(0, 111, 227, 0.1);
            border: 1px solid rgba(0, 111, 227, 0.2);
            color: #006fe3;
        }

        .msg-admin .avatar-box {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            color: #94a3b8;
        }

        /* --- INPUT AREA --- */
        .chat-footer {
            padding: 24px 30px;
            background: rgba(255,255,255,0.2);
        }

        .input-pill {
            background: #ffffff;
            border: 1px solid rgba(0, 111, 227, 0.1);
            border-radius: 20px;
            padding: 8px 8px 8px 24px;
            display: flex;
            align-items: center;
            gap: 12px;
            box-shadow: 1px 10px 20px rgba(191,201,216,0.2);
            transition: all 0.3s;
        }

        .input-pill:focus-within {
            border-color: #006fe3;
            box-shadow: 0 10px 40px rgba(0, 111, 227, 0.1);
        }

        .chat-input {
            background: transparent;
            border: none;
            outline: none;
            flex: 1;
            font-weight: 500;
            color: #1e293b;
            font-size: 14px;
        }

        .btn-send {
            width: 42px;
            height: 42px;
            background: #006fe3;
            border: none;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #fff;
            cursor: pointer;
            transition: all 0.3s;
            box-shadow: 0 4px 12px rgba(0, 111, 227, 0.3);
        }

        .btn-send:hover {
            background: #0058b0;
            transform: scale(1.05);
        }

        .btn-upload {
            width: 42px;
            height: 42px;
            background: transparent;
            border: 1px solid rgba(0, 111, 227, 0.2);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #006fe3;
            cursor: pointer;
            transition: all 0.3s;
        }

        .btn-upload:hover {
            background: rgba(0, 111, 227, 0.1);
        }

        .preview-box {
            position: absolute;
            bottom: calc(100% + 15px);
            left: 30px;
            background: #fff;
            padding: 10px;
            border-radius: 12px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.1);
            display: flex;
            align-items: center;
            gap: 15px;
            border: 1px solid rgba(0, 111, 227, 0.1);
            animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
        }

        .preview-img {
            width: 45px;
            height: 45px;
            border-radius: 8px;
            object-fit: cover;
        }

        /* RESPONSIVE */
        @media (max-width: 768px) {
            .db-scene { padding: 15px 10px; }
            .chat-container { height: calc(100vh - 120px); border-radius: 30px; }
            .chat-header { padding: 15px 20px; }
            .chat-body { padding: 20px; }
            .chat-footer { padding: 15px 20px; }
            .bubble { padding: 12px 18px; font-size: 13px; max-width: 90%; }
            .avatar-box { width: 34px; height: 34px; font-size: 14px; }
        }

        @media (max-width: 480px) {
             .header-text h2 { font-size: 15px; }
             .btn-back { padding: 8px 12px; font-size: 9px; }
        }

        /* Scrollbar */
        .chat-body::-webkit-scrollbar { width: 6px; }
        .chat-body::-webkit-scrollbar-track { background: transparent; }
        .chat-body::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.05); border-radius: 10px; }

        .wait-notice {
            background: rgba(0, 111, 227, 0.05);
            border: 1px solid rgba(0, 111, 227, 0.15);
            padding: 12px 18px;
            border-radius: 12px;
            color: #006fe3;
            font-size: 13px;
            font-weight: 700;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            flex-shrink: 0;
            margin-bottom: 4px;
        }
    `;
    console.log("imageBase64", messages[0]?.image);

    return (
        <div className="body-bg">
            <style>{CSS}</style>
            <Header />

            <section className="nftmax-adashboard nftmax-show">
                <div className="db-scene" style={{ borderRadius: '29px' }}>
                    <div className="container">

                        {/* Upper Nav */}
                        <div className="back-nav">
                            <Link to="/support/my-tickets" className="btn-back">
                                <LuArrowLeft size={16} /> Back to Registry
                            </Link>
                            <div className="ticket-id-tag">
                                {ticketId} <div className="status-dot" />
                            </div>
                        </div>

                        {/* Main Interaction Interface */}
                        <div className="chat-container">
                            <div className="chat-header">
                                <div className="chat-icon-box">
                                    <LuMessageSquare />
                                </div>
                                <div className="header-text text-start">
                                    <h2>Transmission Interface</h2>
                                    <p>Authorized Data Exchange Channel</p>
                                </div>
                                <div className="ms-auto">
                                    {/* <LuMoreVertical style={{ color: '#94a3b8', cursor: 'pointer' }} /> */}
                                </div>
                            </div>

                            <div className="chat-body">
                                <div className="wait-notice">
                                    <LuInfo size={18} />
                                    Please wait, your problem will be answered within 24-48 hours.
                                </div>

                                {messages.length === 0 && (
                                    <div className="text-center py-5 text-gray-500 text-sm fw-bold">No messages found.</div>
                                )}
                                {messages.map((msg, idx) => {
                                    const isUser = msg.role === 'user';
                                    const roleClass = isUser ? 'msg-user' : 'msg-admin';
                                    const timeStr = new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                                    return (
                                        <div key={msg._id || idx} className={`msg-row ${roleClass}`}>
                                            {!isUser && (
                                                <div className="avatar-box">
                                                    <LuUser />
                                                </div>
                                            )}
                                            <div className="bubble-wrap">
                                                <div className="bubble">
                                                    {msg?.image && (
                                                        <div className="text-center mb-4">
                                                            <img
                                                                src={msg?.image}
                                                                alt="ticket attachment"
                                                                style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '16px', border: '1px solid #ddd' }}
                                                            />
                                                        </div>
                                                    )}
                                                    {msg.content}
                                                </div>
                                                <div className="msg-meta">
                                                    {isUser ? 'YOU' : 'ADMIN'} {timeStr !== "Invalid Date" ? timeStr : ""}
                                                </div>
                                            </div>
                                            {isUser && (
                                                <div className="avatar-box">
                                                    <LuUser />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <div ref={chatEndRef} />
                            </div>

                            <div className="chat-footer" style={{ position: 'relative' }}>
                                {image && (
                                    <div className="preview-box">
                                        <img src={imageBase64} alt="preview" className="preview-img" />
                                        <div>
                                            <div style={{ fontSize: '11px', fontWeight: '800', color: '#1e293b' }}>{image.name}</div>
                                            <div style={{ fontSize: '9px', fontWeight: '700', color: '#94a3b8' }}>{(image.size / 1024).toFixed(0)} KB SIZE</div>
                                        </div>
                                        <button type="button" onClick={removeImage} style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: 'none', borderRadius: '6px', width: '24px', height: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                            <LuX size={14} />
                                        </button>
                                    </div>
                                )}
                                <form className="input-pill" onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
                                    <label className="btn-upload" title="Attach Image">
                                        <LuImage size={20} />
                                        <input type="file" className="d-none" accept="image/*" onChange={handleFileChange} />
                                    </label>
                                    <input
                                        type="text"
                                        className="chat-input"
                                        placeholder="Type your message..."
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                    />
                                    <button type="submit" className="btn-send">
                                        <LuSend size={20} />
                                    </button>
                                </form>
                            </div>
                        </div>

                    </div>
                </div>
            </section>
        </div>
    );
}

export default TicketDetails;
